import { ThemeMode } from './config-web';

export interface ConfigBaseProps {
  persistNavigation: 'always' | 'dev' | 'prod' | 'never';
  catchErrors: 'always' | 'dev' | 'prod' | 'never';
  APP_DEFAULT_PATH: string;
  ThemeMode: typeof ThemeMode;
  exitRoutes: string[];
  API_URL?: string;
  FIREBASE_API_KEY?: string;
  FIREBASE_AUTH_DOMAIN?: string;
  FIREBASE_PROJECT_ID?: string;
  FIREBASE_STORAGE_BUCKET?: string;
  FIREBASE_MESSAGING_SENDER_ID?: string;
  FIREBASE_APP_ID?: string;
  FIREBASE_MEASUREMENT_ID?: string;
  FIREBASE_AUTH_EMULATOR_URL?: string;
  RECAPTCHA_SITE_KEY?: string;
  AUTH_URL?: string;
  APP_URL?: string;
  GA_AUTH_TRACKING_ID?: string;
  GA_WEB_TRACKING_ID?: string;
  SENTRY_DSN?: string;
  ASSETRON_SUPPORT_EMAIL?: string;
  ASSETRON_GOOGLE_CLIENT_ID?: string;
  FEATURE_REQUEST_FORM_URL?: string;
  // Application Insights Configuration
  APPINSIGHTS_CONNECTION_STRING?: string;
  APP_VERSION?: string;
  ENVIRONMENT?: string;
  REGION?: string;
  API_VERSION?: string;
  VERBOSE_LOGGING?: string;
  LOG_LEVEL?: string;
  STRIPE_PRICING_TABLE_ID?: string;
  STRIPE_PUBLISHABLE_KEY?: string;
  ASSETRON_SALESIQ_DOMAIN?: string;
  TRACKING_AGENT_MACOS_PKG_URL?: string;
  TRACKING_AGENT_PACKAGE_BASE_URL?: string;
  TRACKING_AGENT_INSTALL_SCRIPT_URL?: string;
  /**
   * Firebase Cloud Messaging web push VAPID public key. Obtained from the
   * Firebase Console under Project Settings → Cloud Messaging → Web
   * configuration → "Web push certificates". Used by the Firebase JS SDK
   * in `browserPush.ts` to call `getToken({ vapidKey })`. NOT one we
   * manage — it's issued by Firebase itself per project.
   */
  FIREBASE_WEB_PUSH_VAPID_KEY?: string;
  ZOHO_DESK_APP_ID?: string;
  ZOHO_DESK_ORG_ID?: string;
  ZOHO_DESK_DEPT_ID?: string;
  ZOHO_DESK_LAYOUT_ID?: string;
  USE_FIREBASE_EMULATOR?: string;
  FIREBASE_AUTH_EMULATOR_HOST?: string;
  ENABLE_TEST_AUTOMATION_HEADER?: string;
  DISABLE_APP_CHECK_CI?: string;
}

export type PersistNavigationConfig = ConfigBaseProps['persistNavigation'];

const isLocalHost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1';

const deriveApiUrlFromCurrentOrigin = (): string | undefined => {
  if (globalThis.window === undefined) {
    return undefined;
  }

  const { protocol, hostname } = globalThis.window.location;
  if (isLocalHost(hostname)) {
    return '';
  }

  const hostParts = hostname.split('.');
  if (
    hostParts.length >= 2 &&
    (hostParts[0] === 'app' || hostParts[0] === 'auth')
  ) {
    hostParts[0] = 'api';
    return `${protocol}//${hostParts.join('.')}`;
  }

  return undefined;
};

const resolveApiUrl = (): string | undefined => {
  const configuredApiUrl = process.env.ASSETRON_API_URL?.trim();
  if (configuredApiUrl) {
    return configuredApiUrl;
  }

  // When localhost has no explicit API_URL configured, use same-origin (/api/*)
  // so sandbox/local-dev flows can rely on the Vite reverse proxy.
  const derivedApiUrl = deriveApiUrlFromCurrentOrigin();
  if (derivedApiUrl === '') {
    return derivedApiUrl;
  }

  return derivedApiUrl;
};

const BaseConfig: ConfigBaseProps = {
  // This feature is particularly useful in development mode, but
  // can be used in production as well if you prefer.
  persistNavigation: 'dev',

  /**
   * Only enable if we're catching errors in the right environment
   */
  catchErrors: 'always',

  /**
   * This is a list of all the route names that will exit the app if the back button
   * is pressed while in that screen. Only affects Android.
   */
  exitRoutes: ['Welcome'],

  APP_DEFAULT_PATH: '/',
  ThemeMode,

  // Prefer an explicit API_URL when configured (for example localhost FE against
  // the shared dev API). Otherwise, localhost falls back to same-origin /api/*
  // so sandbox/local-dev flows can use the Vite reverse proxy.
  API_URL: resolveApiUrl(),
  FIREBASE_API_KEY: process.env.ASSETRON_FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN: process.env.ASSETRON_FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID: process.env.ASSETRON_FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: process.env.ASSETRON_FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID:
    process.env.ASSETRON_FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID: process.env.ASSETRON_FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID: process.env.ASSETRON_FIREBASE_MEASUREMENT_ID,
  FIREBASE_AUTH_EMULATOR_URL: process.env.ASSETRON_FIREBASE_AUTH_EMULATOR_URL,
  RECAPTCHA_SITE_KEY: process.env.ASSETRON_RECAPTCHA_SITE_KEY,
  AUTH_URL: process.env.ASSETRON_AUTH_URL,
  APP_URL: process.env.ASSETRON_APP_URL,
  GA_AUTH_TRACKING_ID: process.env.ASSETRON_GA_AUTH_TRACKING_ID,
  GA_WEB_TRACKING_ID: process.env.ASSETRON_GA_WEB_TRACKING_ID,
  SENTRY_DSN: process.env.ASSETRON_SENTRY_DSN,
  ASSETRON_SUPPORT_EMAIL: process.env.ASSETRON_SUPPORT_EMAIL,
  ASSETRON_GOOGLE_CLIENT_ID: process.env.ASSETRON_GOOGLE_CLIENT_ID,
  FEATURE_REQUEST_FORM_URL: process.env.ASSETRON_FEATURE_REQUEST_FORM_URL,
  APPINSIGHTS_CONNECTION_STRING:
    process.env.ASSETRON_APPINSIGHTS_CONNECTION_STRING,
  APP_VERSION: process.env.ASSETRON_APP_VERSION,
  ENVIRONMENT: process.env.ASSETRON_ENVIRONMENT,
  REGION: process.env.ASSETRON_REGION,
  API_VERSION: process.env.ASSETRON_API_VERSION,
  VERBOSE_LOGGING: process.env.ASSETRON_VERBOSE_LOGGING,
  LOG_LEVEL: process.env.ASSETRON_LOG_LEVEL,
  STRIPE_PRICING_TABLE_ID: process.env.ASSETRON_STRIPE_PRICING_TABLE_ID,
  STRIPE_PUBLISHABLE_KEY: process.env.ASSETRON_STRIPE_PUBLISHABLE_KEY,
  ASSETRON_SALESIQ_DOMAIN: process.env.ASSETRON_SALESIQ_DOMAIN,
  TRACKING_AGENT_MACOS_PKG_URL:
    process.env.ASSETRON_TRACKING_AGENT_MACOS_PKG_URL,
  TRACKING_AGENT_PACKAGE_BASE_URL:
    process.env.ASSETRON_TRACKING_AGENT_PACKAGE_BASE_URL,
  TRACKING_AGENT_INSTALL_SCRIPT_URL:
    process.env.ASSETRON_TRACKING_AGENT_INSTALL_SCRIPT_URL,
  FIREBASE_WEB_PUSH_VAPID_KEY: process.env.ASSETRON_FIREBASE_WEB_PUSH_VAPID_KEY,
  ZOHO_DESK_APP_ID: process.env.ASSETRON_ZOHO_DESK_APP_ID,
  ZOHO_DESK_ORG_ID: process.env.ASSETRON_ZOHO_DESK_ORG_ID,
  ZOHO_DESK_DEPT_ID: process.env.ASSETRON_ZOHO_DESK_DEPT_ID,
  ZOHO_DESK_LAYOUT_ID: process.env.ASSETRON_ZOHO_DESK_LAYOUT_ID,
  USE_FIREBASE_EMULATOR: process.env.ASSETRON_USE_FIREBASE_EMULATOR,
  FIREBASE_AUTH_EMULATOR_HOST: process.env.ASSETRON_FIREBASE_AUTH_EMULATOR_HOST,
  ENABLE_TEST_AUTOMATION_HEADER:
    process.env.ASSETRON_ENABLE_TEST_AUTOMATION_HEADER,
  DISABLE_APP_CHECK_CI: process.env.ASSETRON_DISABLE_APP_CHECK_CI,
};

export default BaseConfig;
